// PR-AM — Per-tenant favicon driven by hostname-resolved org.
//
// Reads `app_icon_url` from PublicOrgContext (resolved BEFORE auth via
// PublicOrgProvider) and injects the URL into <link rel="icon"> +
// <link rel="apple-touch-icon">.
//
// Design choices per Rickard's spec:
//   - Default = NO favicon (not Boply's, not Actionpadel's). If org hasn't
//     uploaded one, we REMOVE the link tags entirely.
//   - Same URL for icon + apple-touch-icon (Q3: simplifies admin UX).
//     Upload pipeline auto-generates 512×512 PNG which works for both.
//   - Pre-resolve state: link tags untouched. With index.html shipping
//     no <link rel="icon">, this means no favicon flash before resolve.
//
// Replaces useDynamicFavicon which read from useOrganization (post-auth)
// and missed the public-page case (= the original bug).

import { useEffect } from "react";
import { usePublicOrgContext } from "@/contexts/PublicOrgContext";

const ICON_RELS = ["icon", "apple-touch-icon"] as const;

function setIconLink(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  if (link.href !== href) {
    link.href = href;
  }
}

function removeIconLink(rel: string) {
  const link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (link?.parentNode) {
    link.parentNode.removeChild(link);
  }
}

export function usePublicFavicon() {
  const { org, isLoading } = usePublicOrgContext();
  const appIconUrl = org?.app_icon_url ?? null;

  useEffect(() => {
    // Pre-resolve: don't touch link tags (avoids flicker between any
    // browser-cached favicon and the eventual injected one).
    if (isLoading) return;

    if (appIconUrl) {
      for (const rel of ICON_RELS) setIconLink(rel, appIconUrl);
    } else {
      // Org resolved but no icon uploaded → remove any stale tags.
      // Spec: "Default: ingen favicon tills hall satt en".
      for (const rel of ICON_RELS) removeIconLink(rel);
    }
  }, [appIconUrl, isLoading]);
}
